import { useCallback, useEffect, useMemo, useState } from "react";
import {
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { AddRounded, ContentCopyRounded } from "@mui/icons-material";
import { toast } from "react-toastify";
import PageHeader from "../components/PageHeader";
import DataTable from "../components/DataTable";
import { Button, IconButton } from "../components/Buttons";
import { createApiKey, listApiKeys, listOrgRoles, revokeApiKey } from "../api";
import { formatDate, formatDateTime } from "../utils/format";
import { apiError } from "../utils/meta";

const MONO = "'SF Mono', Menlo, monospace";

async function copyToClipboard(text, label) {
    try {
        await navigator.clipboard.writeText(text);
        toast.success(`${label} copied`);
    } catch {
        toast.error("Could not copy — select and copy it manually");
    }
}

/* ------------------------------------------------------------------ */
/* One-time key reveal                                                  */
/* ------------------------------------------------------------------ */

function KeyRevealDialog({ secretKey, onClose }) {
    return (
        <Dialog open={!!secretKey} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ fontFamily: "'Newsreader', Georgia, serif" }}>
                Your new API key
            </DialogTitle>
            <DialogContent>
                <Stack sx={{ gap: 2, pt: 0.5 }}>
                    <Typography
                        sx={{ fontSize: 14, color: "var(--text-secondary)" }}
                    >
                        Copy it now — for security it is never shown again. If
                        you lose it, revoke this key and create a new one.
                    </Typography>
                    <Stack
                        sx={{
                            flexDirection: "row",
                            alignItems: "center",
                            gap: 1,
                            background: "var(--bg-tint-soft)",
                            border: "1px solid var(--border-mid)",
                            borderRadius: "3px",
                            p: "10px 14px",
                        }}
                    >
                        <Typography
                            sx={{
                                fontFamily: MONO,
                                fontSize: 13,
                                wordBreak: "break-all",
                                flex: 1,
                            }}
                        >
                            {secretKey}
                        </Typography>
                        <IconButton
                            size="32px"
                            variant="text"
                            title="Copy key"
                            onClick={() =>
                                copyToClipboard(secretKey, "API key")
                            }
                        >
                            <ContentCopyRounded sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Stack>
                    <Typography
                        sx={{ fontSize: 13, color: "var(--warning)" }}
                    >
                        This is the only time the full key is visible.
                    </Typography>
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button variant="contained" color="primary" onClick={onClose}>
                    I've copied it
                </Button>
            </DialogActions>
        </Dialog>
    );
}

/* ------------------------------------------------------------------ */
/* Page                                                                 */
/* ------------------------------------------------------------------ */

export default function AdminApiKeys() {
    const [rows, setRows] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);
    const [form, setForm] = useState({ name: "", orgRoleId: "" });
    const [creating, setCreating] = useState(false);
    const [revealed, setRevealed] = useState(null);
    const [revoking, setRevoking] = useState(null);
    const [revokeBusy, setRevokeBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await listApiKeys();
            setRows(Array.isArray(data) ? data : (data?.data ?? []));
        } catch (err) {
            toast.error(apiError(err, "Failed to load API keys"));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
        (async () => {
            try {
                const { data } = await listOrgRoles();
                setRoles(Array.isArray(data) ? data : (data?.data ?? []));
            } catch (error) {
                console.error("Failed to load roles", { error });
            }
        })();
    }, [load]);

    async function handleCreate() {
        if (!form.name.trim() || !form.orgRoleId) {
            toast.error("Name and role are required");
            return;
        }
        setCreating(true);
        try {
            const { data } = await createApiKey({
                name: form.name.trim(),
                orgRoleId: form.orgRoleId,
            });
            setCreateOpen(false);
            setRevealed(data?.key ?? null);
            load();
        } catch (err) {
            toast.error(apiError(err, "Could not create the key"));
        } finally {
            setCreating(false);
        }
    }

    async function handleRevoke() {
        if (!revoking) return;
        setRevokeBusy(true);
        try {
            await revokeApiKey(revoking.id);
            toast.success(`Key "${revoking.name}" revoked`);
            setRevoking(null);
            load();
        } catch (err) {
            toast.error(apiError(err, "Could not revoke the key"));
        } finally {
            setRevokeBusy(false);
        }
    }

    const columns = useMemo(
        () => [
            {
                key: "name",
                label: "Name",
                render: (row) => (
                    <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                        {row.name}
                    </Typography>
                ),
            },
            {
                key: "prefix",
                label: "Key",
                render: (row) => (
                    <Typography sx={{ fontFamily: MONO, fontSize: 13 }}>
                        {row.prefix}…
                    </Typography>
                ),
            },
            { key: "roleName", label: "Role" },
            { key: "createdByName", label: "Created by" },
            {
                key: "lastUsedAt",
                label: "Last used",
                render: (row) =>
                    row.lastUsedAt ? formatDateTime(row.lastUsedAt) : "Never",
            },
            {
                key: "createdAt",
                label: "Created",
                render: (row) => formatDate(row.createdAt),
            },
            {
                key: "status",
                label: "Status",
                render: (row) =>
                    row.revokedAt ? (
                        <Chip
                            size="small"
                            label="Revoked"
                            sx={{
                                height: 22,
                                color: "var(--error)",
                                background: "transparent",
                                border: "1px solid var(--error)",
                                fontWeight: 600,
                            }}
                        />
                    ) : (
                        <Chip
                            size="small"
                            label="Active"
                            sx={{
                                height: 22,
                                color: "var(--success)",
                                background: "transparent",
                                border: "1px solid var(--success)",
                                fontWeight: 600,
                            }}
                        />
                    ),
            },
            {
                key: "actions",
                label: "",
                align: "right",
                render: (row) =>
                    row.revokedAt ? null : (
                        <Button
                            variant="text"
                            color="error"
                            onClick={() => setRevoking(row)}
                            sx={{ height: 30, fontSize: 13 }}
                        >
                            Revoke
                        </Button>
                    ),
            },
        ],
        [],
    );

    return (
        <Stack>
            <PageHeader
                title="API Keys"
                subtitle="Keys act with the permissions of the role they're bound to"
                action={
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddRounded />}
                        onClick={() => {
                            setForm({ name: "", orgRoleId: "" });
                            setCreateOpen(true);
                        }}
                    >
                        New key
                    </Button>
                }
            />

            <Stack sx={{ gap: 3 }}>
                <DataTable
                    columns={columns}
                    rows={rows}
                    loading={loading}
                    emptyMessage="No API keys yet — create one to call Tessera from your own code"
                />

                {/* Using the API — landing-page style dark terminal block */}
                <Stack sx={{ gap: 1, maxWidth: 760 }}>
                    <Typography
                        sx={{
                            fontSize: 11,
                            letterSpacing: "2px",
                            fontWeight: 700,
                            color: "var(--text-tertiary)",
                        }}
                    >
                        USING THE API
                    </Typography>
                    <Stack
                        sx={{
                            background: "#171715",
                            border: "1px solid #383632",
                            borderRadius: "6px",
                            overflow: "hidden",
                        }}
                    >
                        <Stack
                            sx={{
                                flexDirection: "row",
                                alignItems: "center",
                                gap: 1,
                                p: "10px 16px",
                                borderBottom: "1px solid #383632",
                            }}
                        >
                            <Stack sx={{ flexDirection: "row", gap: "7px" }}>
                                {[0, 1, 2].map((i) => (
                                    <Stack
                                        key={i}
                                        sx={{
                                            width: 10,
                                            height: 10,
                                            borderRadius: "50%",
                                            background: "#4a4742",
                                        }}
                                    />
                                ))}
                            </Stack>
                            <Typography
                                sx={{
                                    fontFamily: MONO,
                                    fontSize: 12,
                                    color: "#8b8781",
                                }}
                            >
                                terminal
                            </Typography>
                        </Stack>
                        <Typography
                            component="pre"
                            sx={{
                                m: 0,
                                p: "20px 24px",
                                fontFamily: MONO,
                                fontSize: 13,
                                lineHeight: 1.9,
                                color: "#d8d5cf",
                                whiteSpace: "pre",
                                overflowX: "auto",
                            }}
                        >
                            {'curl -H "x-api-key: '}
                            <Typography
                                component="span"
                                sx={{ color: "#4caf87", fontFamily: MONO }}
                            >
                                tessera_live_…
                            </Typography>
                            {'" \\\n  https://your-host/api/crm/o/'}
                            <Typography
                                component="span"
                                sx={{ color: "#8b8781", fontFamily: MONO }}
                            >
                                {"{object}"}
                            </Typography>
                            {"/records"}
                        </Typography>
                    </Stack>
                    <Typography
                        sx={{ fontSize: 13, color: "var(--text-secondary)" }}
                    >
                        Every generic record endpoint accepts the{" "}
                        <code>x-api-key</code> header — the key's role decides
                        what it can read and write.
                    </Typography>
                </Stack>
            </Stack>

            {/* Create dialog */}
            <Dialog
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle
                    sx={{ fontFamily: "'Newsreader', Georgia, serif" }}
                >
                    New API key
                </DialogTitle>
                <DialogContent>
                    <Stack sx={{ gap: 2, pt: 1 }}>
                        <TextField
                            label="Name"
                            placeholder="e.g. Zapier integration"
                            value={form.name}
                            onChange={(e) =>
                                setForm((p) => ({ ...p, name: e.target.value }))
                            }
                            required
                            fullWidth
                            autoFocus
                        />
                        <TextField
                            label="Role"
                            select
                            value={form.orgRoleId}
                            onChange={(e) =>
                                setForm((p) => ({
                                    ...p,
                                    orgRoleId: e.target.value,
                                }))
                            }
                            required
                            fullWidth
                            helperText="The key can do exactly what this role can do"
                        >
                            {roles.map((r) => (
                                <MenuItem key={r.id} value={r.id}>
                                    {r.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button
                        variant="soft-outlined"
                        onClick={() => setCreateOpen(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        loading={creating}
                        onClick={handleCreate}
                    >
                        Create key
                    </Button>
                </DialogActions>
            </Dialog>

            {/* One-time reveal */}
            <KeyRevealDialog
                secretKey={revealed}
                onClose={() => setRevealed(null)}
            />

            {/* Revoke confirm */}
            <Dialog open={!!revoking} onClose={() => setRevoking(null)}>
                <DialogTitle
                    sx={{ fontFamily: "'Newsreader', Georgia, serif" }}
                >
                    Revoke this key?
                </DialogTitle>
                <DialogContent>
                    <Typography
                        sx={{ fontSize: 14.5, color: "var(--text-secondary)" }}
                    >
                        &#8220;{revoking?.name}&#8221; will stop working
                        immediately. Anything calling the API with it will get
                        401s.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button
                        variant="soft-outlined"
                        onClick={() => setRevoking(null)}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        loading={revokeBusy}
                        onClick={handleRevoke}
                    >
                        Revoke key
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}
